export type QuizClass = "theory" | "coding";

export interface SubjectProblem {
  class: QuizClass;
  subject: string;
  total_count: {
    min: number;
    max: number;
  };
}

export interface ScopeConfig {
  scope: string;
  count: number;
}

export interface SubjectScope {
  subject: string;
  scopes: ScopeConfig[];
}

export interface SubjectCountAllocation {
  subject: string;
  count: number;
  min: number;
  max: number;
}

type RandomSource = () => number;

function weightedRandomSelect<T extends { count: number }>(
  items: T[],
  count: number,
  random: RandomSource
): T[] {
  const totalWeight = items.reduce((sum, item) => sum + item.count, 0);
  if (totalWeight <= 0) return [];

  const selected: T[] = [];
  for (let i = 0; i < count; i++) {
    const target = random() * totalWeight;
    let cumulativeWeight = 0;

    for (const item of items) {
      cumulativeWeight += item.count;
      if (target <= cumulativeWeight) {
        selected.push(item);
        break;
      }
    }
  }

  return selected;
}

const randomIndex = (length: number, random: RandomSource): number => {
  return Math.min(length - 1, Math.floor(random() * length));
};

const randomIntegerInclusive = (
  min: number,
  max: number,
  random: RandomSource
): number => {
  return Math.floor(random() * (max - min + 1)) + min;
};

export function computeQuizTotalCounts(
  classes: QuizClass[],
  random: RandomSource = Math.random
): Record<string, number> {
  const counts: Record<string, number> = {};

  if (classes.length === 1) {
    const singleClass = classes[0];
    if (singleClass === "theory") {
      counts.theory = random() < 0.5 ? 11 : 12;
    } else if (singleClass === "coding") {
      counts.coding = 10;
    }
  } else {
    const theoryCount = random() < 0.5 ? 11 : 12;
    counts.theory = theoryCount;
    counts.coding = theoryCount === 11 ? 9 : 8;
  }

  return counts;
}

export function allocateSubjectCounts(
  classSubjects: SubjectProblem[],
  totalCount: number,
  random: RandomSource = Math.random
): SubjectCountAllocation[] {
  const subjectCounts = classSubjects.map((sp) => {
    const min = sp.total_count.min;
    const max = sp.total_count.max;
    const count = randomIntegerInclusive(min, max, random);

    return { subject: sp.subject, count, min, max };
  });

  let totalAssigned = subjectCounts.reduce((sum, sc) => sum + sc.count, 0);

  while (totalAssigned > totalCount) {
    const reducibleSubjects = subjectCounts.filter((sc) => sc.count > sc.min);

    if (reducibleSubjects.length === 0) {
      break;
    }

    const selected = reducibleSubjects[randomIndex(reducibleSubjects.length, random)];
    const index = subjectCounts.findIndex((sc) => sc.subject === selected.subject);
    if (index !== -1) {
      subjectCounts[index].count--;
      totalAssigned--;
    }
  }

  while (totalAssigned < totalCount) {
    const expandableSubjects = subjectCounts.filter((sc) => sc.count < sc.max);

    if (expandableSubjects.length === 0) {
      break;
    }

    const selected = expandableSubjects[randomIndex(expandableSubjects.length, random)];
    const index = subjectCounts.findIndex((sc) => sc.subject === selected.subject);
    if (index !== -1) {
      subjectCounts[index].count++;
      totalAssigned++;
    }
  }

  return subjectCounts;
}

export function selectQuizScopes(
  subjectsProblems: SubjectProblem[],
  subjectScopes: SubjectScope[],
  classes: QuizClass[],
  random: RandomSource = Math.random
): ScopeConfig[] {
  const totalCounts = computeQuizTotalCounts(classes, random);
  const selectedScopes: ScopeConfig[] = [];

  for (const currentClass of classes) {
    const totalCount = totalCounts[currentClass];
    if (!totalCount) continue;

    const classSubjects = subjectsProblems.filter(
      (subjectProblem) => subjectProblem.class === currentClass
    );
    const subjectCounts = allocateSubjectCounts(
      classSubjects,
      totalCount,
      random
    );
    const totalAssigned = subjectCounts.reduce(
      (sum, subjectCount) => sum + subjectCount.count,
      0
    );

    if (totalAssigned !== totalCount) {
      console.warn(
        `QuizProblems ${currentClass} 문제 수를 목표 ${totalCount}개에 맞추지 못했습니다. 실제 할당: ${totalAssigned}개`
      );
    }

    for (const subjectCount of subjectCounts) {
      if (subjectCount.count === 0) continue;

      const subjectScope = subjectScopes.find(
        (scopeConfig) =>
          scopeConfig.subject.toLowerCase() ===
          subjectCount.subject.toLowerCase()
      );

      if (!subjectScope || subjectScope.scopes.length === 0) {
        console.warn(
          `과목 ${subjectCount.subject}에 대한 scope 정보를 찾을 수 없습니다.`
        );
        continue;
      }

      const scopeCounts = new Map<string, number>();
      const weightedScopes = weightedRandomSelect(
        subjectScope.scopes,
        subjectCount.count,
        random
      );

      for (const weightedScope of weightedScopes) {
        scopeCounts.set(
          weightedScope.scope,
          (scopeCounts.get(weightedScope.scope) ?? 0) + 1
        );
      }

      for (const [scope, count] of scopeCounts) {
        selectedScopes.push({ scope, count });
      }
    }
  }

  return selectedScopes;
}
